<script context="module">export const META = {
  height: 200,
  topMargin: 18,
  bottomMargin: 0,
  leftMargin: 100,
  // ...when font-size set to 12px and 7 character ticks
  rightMargin: 250
};
</script>

<script>import * as d3 from "d3";
import { okhsl } from "../../../d3-mapp/color/index.js";
import ScalarAxis from "../ScalarAxis.svelte";
import GraphPlot from "../GraphPlot.svelte";
import SignalProjection from "./SignalProjection.svelte";
import SignalHistogram from "./SignalHistogram.svelte";
import {
} from "../types.js";
import {
  UNDEFINED_TAG,
  UNTAGGED_TAG,
  getTag,
  splineToPath
} from "../../../dsp/signal/helpers/to-path.js";
import { getSplineDomain } from "../../../dsp/spline.js";
import {
  CubicBezier
} from "../../../d3-mapp/curve/bezier.js";
import { findIntersections } from "../../../dsp/signal/helpers/spline-intersections.js";
export let xScale;
export const timeAxisDefinition = void 0;
export let splines;
export let title;
export let yAxisLabel;
export let colors = [okhsl("hsl(0,0%,0%,1.0)")];
export let undefinedColors = [okhsl("hsl(0,0%,30%,1.0)")];
export let leftIndent = 0;
export let rightIndent = 0;
export let scalarTickTextMetrics = void 0;
export let locale;
export let scalarEther = true;
export let showAxis = true;
export let showThreshold = true;
export let showProjection = true;
export let showHistogram = true;
export let projectionHeight = 10;
export let thresholds = void 0;
export let height = META.height;
let yScale;
let _thresholds = [];
let paths = [];
let thresholdMap;
let projectionData;
let histogramData;
const topMargin = META.topMargin;
let tagProjectionHeight = showProjection ? projectionHeight : 0;
let plotHeight = height - topMargin - tagProjectionHeight;
let histogramHeight = height - topMargin;
let histogramWidth = rightIndent;
let ticks = void 0;
const prepareThresholds = (thresholds2, locale2) => {
  return thresholds2 ? thresholds2.map(
    (threshold) => Object.assign({ tag: "_" + threshold.value }, threshold)
  ).map(
    (threshold) => Object.assign({}, threshold, {
      stroke: threshold.stroke.map(
        (stroke) => Object.assign({}, stroke)
      )
    })
  ).sort((t0, t1) => t0.value - t1.value).reverse().map(
    (threshold, i) => Object.assign(threshold, {
      index: i,
      label: `>= ${threshold.value.toLocaleString(locale2)}`
    })
  ) : [];
};
const buildThresholdMap = (thresholds2, untaggedColors, undefinedColors2, locale2) => {
  const thresholdMap2 = thresholds2.reduce(
    (sm, threshold) => {
      sm[threshold.tag] = threshold;
      return sm;
    },
    {}
  );
  thresholdMap2[UNTAGGED_TAG] = {
    value: 0,
    tag: UNTAGGED_TAG,
    label: thresholds2 && thresholds2.length > 0 ? `< ${thresholds2[thresholds2.length - 1].value.toLocaleString(locale2)}` : "",
    index: thresholds2 && thresholds2.length > 0 ? thresholds2[thresholds2.length - 1].index + 1 : 0,
    stroke: untaggedColors.map((color) => {
      return { color };
    })
  };
  thresholdMap2[UNDEFINED_TAG] = {
    value: 0,
    tag: UNDEFINED_TAG,
    label: "?",
    index: thresholdMap2[UNTAGGED_TAG].index + 1,
    stroke: undefinedColors2.map((color) => {
      return { color };
    })
  };
  return thresholdMap2;
};
$: {
  tagProjectionHeight = showProjection ? projectionHeight : 0;
}
$: {
  plotHeight = height - topMargin - tagProjectionHeight;
  histogramHeight = height - topMargin;
  histogramWidth = rightIndent;
  const start = xScale.domain()[0].getTime();
  const end = xScale.domain()[1].getTime();
  let yExtent = splines.map((spline) => getSplineDomain(spline)).reduce(
    (extent, domain) => {
      if (extent) {
        return [
          Math.min(extent[0], domain[0].valueOf()),
          Math.max(extent[1], domain[1].valueOf())
        ];
      } else {
        return domain;
      }
    },
    void 0
  );
  const yRange = [plotHeight + topMargin, topMargin];
  yScale = d3.scaleLinear(yExtent, yRange).nice();
  if (scalarTickTextMetrics) {
    const scalarTickHeight = scalarTickTextMetrics.actualBoundingBoxAscent + scalarTickTextMetrics.actualBoundingBoxDescent;
    const maxTicks = Math.floor(plotHeight / scalarTickHeight);
    ticks = ticks !== void 0 ? Math.min(ticks, maxTicks) : maxTicks;
  }
  paths = splines.map((spline) => splineToPath(spline, _thresholds));
}
$: {
  const intersections = findIntersections(splines);
  const intersectionCoordinates = intersections.flatMap(
    (result) => result.intersections.map((intersection) => ({
      x: result.x,
      y: intersection.y
    }))
  );
}
$: {
  _thresholds = prepareThresholds(showThreshold ? thresholds : [], locale);
  thresholdMap = buildThresholdMap(
    _thresholds,
    colors,
    undefinedColors,
    locale
  );
}
$: {
  projectionData = paths.map((path) => {
    let tags = path.map((pathSection) => ({
      tag: getTag(pathSection),
      start: pathSection.meta.tStart,
      end: pathSection.meta.tEnd
    }));
    return tags;
  });
  let histogramDataDistribution = Object.values(thresholdMap).reduce(
    (distribution, thresDef) => {
      distribution[thresDef.tag] = {
        key: thresDef.tag,
        label: thresDef.label,
        index: thresDef.index,
        color: thresDef.stroke[0].color,
        value: 0,
        count: 0
      };
      return distribution;
    },
    {}
  );
  histogramData = projectionData[0].reduce(
    (pcd, tagProjection) => {
      if (!pcd.distribution[tagProjection.tag]) {
        pcd.distribution[tagProjection.tag] = {
          key: tagProjection.tag,
          label: thresholdMap[tagProjection.tag].label,
          color: thresholdMap[tagProjection.tag].stroke[0].color,
          index: thresholdMap[tagProjection.tag].index,
          value: tagProjection.end - tagProjection.start,
          count: 1
        };
      } else {
        pcd.distribution[tagProjection.tag].value += tagProjection.end - tagProjection.start;
        pcd.distribution[tagProjection.tag].count++;
      }
      pcd.max = pcd.max === void 0 ? tagProjection.tag : pcd.distribution[tagProjection.tag].value > pcd.distribution[pcd.max].value ? tagProjection.tag : pcd.max;
      pcd.min = pcd.min === void 0 ? tagProjection.tag : pcd.distribution[tagProjection.tag].value < pcd.distribution[pcd.min].value ? tagProjection.tag : pcd.min;
      pcd.count++;
      return pcd;
    },
    {
      title: "",
      distribution: histogramDataDistribution,
      count: 0,
      max: void 0,
      min: void 0
    }
  );
  histogramData.distribution = Object.values(histogramData.distribution).sort((d0, d1) => d0.index - d1.index).reduce(
    (distribution, entry) => {
      distribution[entry.key] = entry;
      return distribution;
    },
    {}
  );
}
</script>

<g class="seriesPlot plot">
  {#if title && title.length > 0}
    <title>{title}</title>
  {/if}
  <g
    class={`left ${showAxis ? "" : "hidden"}`}
    transform="translate({leftIndent},0)"
    ><ScalarAxis
      scale={yScale}
      label={yAxisLabel}
      labelSwatch={colors.length === 1 ? colors[0] : undefined}
      ether={scalarEther
        ? xScale.range()[1] - xScale.range()[0]
        : xScale.range()[0] - xScale.range()[1]}
      thresholds={showThreshold ? thresholds : []}
      {ticks}
    /></g
  >
  {#if paths && paths.length > 0}
    <g>
      <GraphPlot
        {paths}
        {xScale}
        {yScale}
        strokes={thresholdMap}
        defaultStroke={{ color: okhsl("black"), width: 1 }}
      />
    </g>
  {/if}
  {#if showProjection}
    <g
      class={`projection ${showProjection ? "" : "hidden"}`}
      transform="translate(0,{topMargin + plotHeight})"
      ><SignalProjection
        data={projectionData}
        strokes={thresholdMap}
        {xScale}
        defaultFill={okhsl("hsl(0,25%,30%,0.5)")}
        height={projectionHeight}
        bind:size={tagProjectionHeight}
      /></g
    >{/if}
  {#if showHistogram && showThreshold}
    <g
      class={`right ${showHistogram ? "" : "hidden"}`}
      transform="translate({xScale.range()[1] + topMargin / 2},{topMargin +
        (height - topMargin - histogramHeight) / 2})"
      ><SignalHistogram
        type="vbar"
        padding={20}
        data={histogramData}
        defaultFill={okhsl("hsl(0,25%,30%,0.5)")}
        width={histogramWidth}
        height={histogramHeight}
      /></g
    >
  {/if}
</g>

<style>
  g.seriesPlot g.left.hidden :global(g.axis text),
  g.seriesPlot g.left.hidden :global(g.axis g.sub-axis g.tick line:not(.ether)),
  g.seriesPlot g.left.hidden :global(g.axis g.sub-axis g.tick text),
  g.seriesPlot g.left.hidden :global(g.axis g.sub-axis path) {
    display: none;
  }

  g.seriesPlot g.right.hidden {
    display: none;
  }

  g.seriesPlot g.projection.hidden {
    display: none;
  }
</style>
